import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CopilotButton from "../../../../components/CopilotButton";
import { ExpandableDetails } from "../../../../components/ExpandableDetails";
import { ConditionToggleFilterChip } from "../../../../components/filters/Condition/ConditionToggleFilterChip";
import { FilterMode } from "../../../../components/filters/Condition/types";
import { ProductFilterValue } from "../../../../components/filters/ProductFilter/types";
import { EmployeesRangeFilterChip } from "../../../../components/filters/Ranges/EmployeesRangeFilterChip";
import { RevenueRangeFilterChip } from "../../../../components/filters/Ranges/RevenueRangeFilterChip";
import { InclusionMethodValue } from "../../../../legacy/types/companyFilter";
import {
  CopilotWorkflow,
  useCopilotStore,
} from "../../../../store/copilot/CopilotStore";
import { useWorkspaceStore } from "../../../../store/workspace/WorkspaceStore";
import { SOMConfig, Workspace } from "../../../../types/workspace";
import { ICPSOMDisplay } from "../../../ICP/components/SAMSOMSection/ICPSOMDisplay";
import { IcpDataBanner } from "../../../WorkspaceConfiguration/IcpDataBanner";
import { IcpNoDataBanner } from "../../../WorkspaceConfiguration/IcpNoDataBanner";
import { ProductFilterChip } from "../../../../components/filters/ProductFilter/ProductFilterChip";

export const Som = () => {
  const somConfiguration = useWorkspaceStore(
    (state) => state.draft?.configuration.som || ({} as SOMConfig)
  );
  const setWorkspace = useWorkspaceStore((state) => state.setDraft);
  const icpSOM = useWorkspaceStore((state) => state.icpSOM);

  const isSomIcpAppliedToConfig = useWorkspaceStore(
    (state) => state.isSomIcpAppliedToConfig
  );
  const isNewIcpAvailableForSom = useWorkspaceStore(
    (state) => state.isNewIcpAvailableForSom
  );
  const markIcpAppliedToSom = useWorkspaceStore(
    (state) => state.markIcpAppliedToSom
  );
  const hasIcpSOMData = icpSOM?.employeeRange || icpSOM?.revenueRange;

  //no deps are needed as we just want it to run one time,adding deps will cause infinite loop
  const initialEmployeeRange = somConfiguration.employeeRange;
  const initialRevenueRange = somConfiguration.revenueRange;

  const highlightedElement = useCopilotStore(
    (state) => state.highlightedElement
  );

  const handleEmployeeRangeChange = (
    min: number | undefined,
    max: number | undefined
  ) => {
    setWorkspace((prev) => ({
      ...(prev as Workspace),
      configuration: {
        ...(prev as Workspace).configuration,
        som: {
          ...(prev as Workspace).configuration.som,
          employeeRange: { min, max },
        },
      },
    }));
  };

  const handleRevenueRangeChange = (
    min: number | undefined,
    max: number | undefined
  ) => {
    setWorkspace((prev) => ({
      ...(prev as Workspace),
      configuration: {
        ...(prev as Workspace).configuration,
        som: {
          ...(prev as Workspace).configuration.som,
          revenueRange: { min, max },
        },
      },
    }));
  };

  const productConditionMode = somConfiguration.productsInclusionMethod;

  const handleProductConditionModeChange = (mode: FilterMode) => {
    if (mode === "NONE_PRESENT") return;

    setWorkspace((prev) => ({
      ...(prev as Workspace),
      configuration: {
        ...(prev as Workspace).configuration,
        som: {
          ...(prev as Workspace).configuration.som,
          productsInclusionMethod: mode,
        },
      },
    }));
  };

  const products = {
    ids: somConfiguration.productIds,
    inclusionMethod: productConditionMode,
  };

  const handleOnSelectedProducts = ({ ids }: ProductFilterValue) => {
    setWorkspace((prev) => ({
      ...(prev as Workspace),
      configuration: {
        ...(prev as Workspace).configuration,
        som: {
          ...(prev as Workspace).configuration.som,
          productIds: ids,
        },
      },
    }));
  };

  const excludedProducts = {
    ids: somConfiguration.excludedProductIds,
    inclusionMethod: "NONE_PRESENT" as InclusionMethodValue,
  };

  const handleOnSelectedExcludedProducts = ({ ids }: ProductFilterValue) => {
    setWorkspace((prev) => ({
      ...(prev as Workspace),
      configuration: {
        ...(prev as Workspace).configuration,
        som: {
          ...(prev as Workspace).configuration.som,
          excludedProductIds: ids,
        },
      },
    }));
  };

  function handleSomOverrideData() {
    if (icpSOM?.employeeRange) {
      handleEmployeeRangeChange(
        icpSOM.employeeRange.min,
        icpSOM.employeeRange.max
      );
    }
    if (icpSOM?.revenueRange) {
      handleRevenueRangeChange(
        icpSOM.revenueRange.min,
        icpSOM.revenueRange.max
      );
    }
    markIcpAppliedToSom();
  }

  return (
    <Box paddingLeft="3px">
      {hasIcpSOMData ? (
        <IcpDataBanner
          configType="SOM"
          isIcpAppliedToConfig={isSomIcpAppliedToConfig}
          isNewIcpAvailable={isNewIcpAvailableForSom}
          overrideDataAction={handleSomOverrideData}
          bannerBody={<ICPSOMDisplay />}
        />
      ) : (
        <IcpNoDataBanner />
      )}
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 2, mt: 2, border: "1px solid #e0e0e0" }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            overflowX: "hidden",
            overflowWrap: "break-word",
            wordWrap: "break-word",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              zIndex: 1,
            }}
          >
            <CopilotButton
              id="copilot-som-button"
              workflow={CopilotWorkflow.SOM}
            />
          </Box>
          <Stack flexDirection="column" gap={1}>
            <Stack
              direction="row"
              gap={0.5}
              flexWrap="wrap"
              maxWidth="100%"
              alignItems="center"
              sx={{ pr: "40px" }}
            >
              <Typography>
                The companies we can realistically serve have an employee count
              </Typography>

              {/* HACK: force break line */}
              <Stack flexBasis="100%" />

              <EmployeesRangeFilterChip
                id="employees-range"
                range={initialEmployeeRange}
                handleRangeChange={handleEmployeeRangeChange}
                highlighted={highlightedElement === "employee_chip"}
              />
              <Typography>, and annual revenue</Typography>
              <RevenueRangeFilterChip
                id="revenue-range"
                range={initialRevenueRange}
                handleRangeChange={handleRevenueRangeChange}
                highlighted={highlightedElement === "revenue_chip"}
              />

              {/* HACK: force break line */}
              <Stack flexBasis="100%" />

              <Typography>and have</Typography>
              <ConditionToggleFilterChip
                id="condition-toggle"
                condition={productConditionMode}
                handleConditionChange={handleProductConditionModeChange}
              />
              <Typography component="span">
                of the following technologies installed
              </Typography>

              {/* HACK: force break line */}
              <Stack flexBasis="100%" />

              <ProductFilterChip
                id="products"
                selectedProducts={products}
                onProductsSelect={handleOnSelectedProducts}
                onDelete={() =>
                  handleOnSelectedProducts({
                    ids: [],
                    inclusionMethod: "ANY_PRESENT",
                  })
                }
              />
            </Stack>
          </Stack>

          <Box sx={{ mt: 2 }}>
            <ExpandableDetails
              id="excluded-products-details"
              isExpanded={excludedProducts.ids?.length > 0}
            >
              <Stack flexDirection="column" gap={1}>
                <Stack
                  direction="row"
                  gap={0.5}
                  flexWrap="wrap"
                  maxWidth="100%"
                  alignItems="center"
                >
                  <Typography>
                    And does not have any of the following technologies
                    installed
                  </Typography>
                  <ProductFilterChip
                    id="excluded-products"
                    selectedProducts={excludedProducts}
                    onProductsSelect={handleOnSelectedExcludedProducts}
                    onDelete={() =>
                      handleOnSelectedExcludedProducts({
                        ids: [],
                        inclusionMethod: "NONE_PRESENT",
                      })
                    }
                  />
                </Stack>
              </Stack>
            </ExpandableDetails>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};
